import { Link } from "react-router-dom";
import { Info, Disc3 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../i18n/translations";
import { Reveal, SectionHeading, Sparkles, PerBadge } from "../components/ui-bits";
import { Marquee } from "../components/Marquee";

const priceRows = [
  { key: "r1", price: "€3,00", game: true },
  { key: "r2", price: "€4,50", game: true },
  { key: "r3", price: "€5,00", game: true },
  { key: "r4", price: "€5,50", game: true },
  { key: "r5", price: "€5,50", game: true },
  { key: "r6", price: "€4,50", game: true },
  { key: "r7", price: "€1,00", game: false },
];

const DiscoSection = () => {
  const { t } = useLanguage();
  return (
    <section className="relative py-28 overflow-hidden grain" data-testid="disco-section">
      <div className="absolute inset-0">
        <img src={IMAGES.crowd} alt="Disco bowling" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05070B] via-[#05070B]/55 to-[#05070B]" />
      </div>
      <div className="light-beam left-[15%]" />
      <div className="light-beam right-[15%]" style={{ animationDelay: "-6s" }} />
      <Sparkles count={16} />
      <div className="hidden md:block disco-ball absolute top-10 right-[8%] w-40 h-40 rounded-full" />
      <div className="relative max-w-4xl mx-auto px-5 text-center">
        <Reveal>
          <h2 className="font-disco text-5xl sm:text-7xl neon-strong neon-flicker leading-tight" data-testid="disco-title">
            {t("disco.title")}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-display tracking-[0.3em] uppercase text-[#F0C971] mt-4">{t("disco.sub")}</p>
          <p className="mt-6 text-[#9db8d6] leading-relaxed max-w-2xl mx-auto">{t("disco.text")}</p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 inline-flex flex-col items-center gap-3 glass rounded-3xl px-12 py-8" data-testid="disco-price">
            <span className="font-disco text-6xl gold-text">{t("disco.price")}</span>
            <PerBadge>{t("prices.perPerson")} / {t("prices.perGame")}</PerBadge>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10">
            <Link to="/reserveer" data-testid="disco-reserve-cta" className="btn-neon font-display text-xl tracking-[0.25em] uppercase px-14 py-5 rounded-full">
              {t("disco.cta")}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Bowling() {
  const { t } = useLanguage();
  return (
    <main className="pt-32 theme-pink">
      <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="pricing-section">
        <SectionHeading eyebrow="Helios" title={t("prices.title")} sub={t("prices.sub")} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {priceRows.map((r, i) => (
            <Reveal key={r.key} delay={i * 0.07}>
              <div data-testid={`price-card-${r.key}`} className="glass glow-card rounded-2xl p-7 h-full flex flex-col">
                <p className="text-[#E2F1FF] font-semibold min-h-[3rem]">{t(`prices.${r.key}`)}</p>
                <p className="font-disco text-5xl neon mt-4">{r.price}</p>
                <div className="mt-4">
                  <PerBadge>
                    {r.game ? `${t("prices.perPerson")} / ${t("prices.perGame")}` : t("prices.perPerson")}
                  </PerBadge>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.5}>
            <div className="rounded-2xl p-7 h-full flex flex-col justify-center border border-[#F0C971]/40 bg-[#F0C971]/5" data-testid="price-note">
              <p className="flex gap-3 text-sm text-[#E2F1FF] leading-relaxed">
                <Info size={20} className="text-[#F0C971] shrink-0 mt-0.5" />
                {t("prices.note")}
              </p>
              <Link to="/reserveer" data-testid="pricing-book-cta" className="btn-neon mt-6 font-display text-center tracking-[0.2em] uppercase px-8 py-3 rounded-full">
                {t("prices.cta")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="pool-darts-pricing">
        <SectionHeading eyebrow="Helios" title={t("prices.poolDartsTitle")} sub={t("prices.poolDartsNote")} />
        <div className="grid sm:grid-cols-3 gap-5">
          <Reveal>
            <div data-testid="price-card-pool-green" className="glass glow-card rounded-2xl overflow-hidden h-full flex flex-col">
              <div className="relative h-36">
                <img src={IMAGES.poolGreenImg} alt="Pool groene tafel" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] to-transparent" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-[#E2F1FF] font-semibold">{t("prices.poolGreen")}</p>
                <p className="font-disco text-5xl neon mt-4">€9</p>
                <div className="mt-4"><PerBadge>{t("prices.poolGreenMeta")}</PerBadge></div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="price-card-pool-red" className="glass glow-card rounded-2xl overflow-hidden h-full flex flex-col">
              <div className="relative h-36">
                <img src={IMAGES.poolReal} alt="Pool rode tafel" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] to-transparent" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-[#E2F1FF] font-semibold">{t("prices.poolRed")}</p>
                <p className="font-disco text-5xl neon mt-4">€2</p>
                <div className="mt-4"><PerBadge>{t("prices.poolRedMeta")}</PerBadge></div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div data-testid="price-card-darts" className="glass glow-card rounded-2xl overflow-hidden h-full flex flex-col">
              <div className="relative h-36">
                <img src={IMAGES.dartsImg} alt="Darts" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] to-transparent" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-[#E2F1FF] font-semibold">{t("prices.dartsRow")}</p>
                <p className="font-disco text-5xl neon mt-4">€5</p>
                <div className="mt-4"><PerBadge>{t("prices.dartsMeta")}</PerBadge></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <Marquee />
      <DiscoSection />
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden group h-80">
              <img src={IMAGES.pins} alt="Bowling pins" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070B]/80 to-transparent" />
              <div className="absolute bottom-6 left-6 flex items-center gap-3">
                <Disc3 className="text-[#00F0FF]" size={28} />
                <span className="font-display text-2xl uppercase tracking-widest neon">{t("quick.lanes")}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative rounded-2xl overflow-hidden group h-80">
              <img src={IMAGES.dartsImg} alt="Pool & Darts" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070B]/80 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <span className="font-display text-2xl uppercase tracking-widest neon">Pool & Darts</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
